import { getEnvironment } from "@common/utils/env";
import { CookieStore, ICookieStore } from "@common/core/CookieStore";

export interface ILogger {
  info(message: string, data?: RecordAny): void;
  warn(message: string, data?: RecordAny): void;
  debug(message: string, data?: RecordAny): void;
  error(message: string, data?: RecordAny | Error | unknown): void;
  createChildLogger(scope: string): ILogger;
}

class Logger implements ILogger {
  constructor(private readonly scope: string, private readonly cookieStore: ICookieStore) {}

  private formatData(data?: RecordAny | Error): RecordAny {
    if (!data) {
      return {};
    }
    if (data instanceof Error) {
      return {
        errorMessage: data.message,
        errorStack: data.stack,
        errorName: data.name
      };
    }
    return data;
  }

  log(level: "info" | "warn" | "error" | "debug", message: string, data?: RecordAny | Error): void {
    const isDev = getEnvironment() !== "production" || this.cookieStore.getCookie("RestLessDev") === "true";
    if (!isDev) {
      // For now, whilst we're only logging to console, use only if we're confident we're in a dev environment or the dev cookie is set
      return;
    }
    const timestamp = new Date().toISOString();
    const logData = {
      timestamp,
      level,
      sessionId: window.RestlessSession?.getSessionId() ?? "no-session",
      scope: this.scope,
      data: this.formatData(data)
    };

    // eslint-disable-next-line no-console -- This is neccessary for the logger.
    console[level](message, logData);
  }

  info(message: string, data?: RecordAny): void {
    this.log("info", message, data);
  }

  warn(message: string, data?: RecordAny): void {
    this.log("warn", message, data);
  }

  error(message: string, data?: RecordAny | Error): void {
    this.log("error", message, data);
  }

  debug(message: string, data?: RecordAny): void {
    this.log("debug", message, data);
  }

  createChildLogger(scope: string): ILogger {
    return new Logger(`${this.scope}.${scope}`, this.cookieStore);
  }
}

const globalLogger: ILogger = new Logger("global", CookieStore);
export { globalLogger };
